// Talleres/piezasTalleres.jsx
//
// Piezas visuales de los modales del módulo Talleres, con el lenguaje del panel
// rediseñado (ficha de usuario, Agenda): cabecera azul marino, indicadores en
// tarjeta blanca, chips por tono y botones redondeados.

import React from 'react';
import { FiX } from 'react-icons/fi';

const TONOS = {
  azul: 'bg-blue-50 text-primary',
  amarillo: 'bg-secondary/40 text-[#5c4300]',
  gris: 'bg-gray-100 text-gray-500',
  rojo: 'bg-red-50 text-red-700',
};

export const Chip = ({ tono = 'gris', icon, children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${TONOS[tono]} ${className}`}>
    {icon}{children}
  </span>
);

export const claseBtnPeligro =
  'inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-200 bg-white text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:pointer-events-none';

// Versiones chicas para las acciones de cada tarjeta de horario.
export const claseBtnChico =
  'inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 px-3.5 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700 text-[13px] font-semibold hover:border-primary hover:!text-primary transition-colors disabled:opacity-50 disabled:pointer-events-none';
export const claseBtnChicoPrimario =
  'inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 px-3.5 py-1.5 rounded-full bg-primary !text-white text-[13px] font-bold shadow-sm hover:brightness-110 transition-all disabled:opacity-60';
export const claseBtnChicoPeligro =
  'inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 px-3.5 py-1.5 rounded-full border border-red-200 bg-white text-red-600 text-[13px] font-semibold hover:bg-red-50 transition-colors';

// Botón redondo solo con ícono (acciones de fila y de bloque).
export const claseBtnIcono =
  'w-8 h-8 inline-flex items-center justify-center rounded-full text-gray-500 hover:bg-blue-50 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none';
export const claseBtnIconoPeligro =
  'w-8 h-8 inline-flex items-center justify-center rounded-full text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40 disabled:pointer-events-none';

export const claseTituloSeccion = 'text-[11px] font-bold uppercase tracking-wider text-gray-500 m-0';

/**
 * Cabecera azul marino de los modales grandes, igual a la de la ficha de usuario.
 * `datos` son los números grandes de abajo ([{ label, value, sub }]).
 */
export function CabeceraModal({ icon, titulo, subtitulo, chips, datos = [], onClose }) {
  return (
    <header className="relative bg-[#00266b] text-white px-6 pt-6 pb-5 overflow-hidden">
      <div aria-hidden className="absolute inset-0 opacity-[.08] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '22px 22px',
      }} />
      <button type="button" onClick={onClose} aria-label="Cerrar"
        className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full text-blue-100 hover:bg-white/10 hover:text-white transition-colors">
        <FiX size={18} />
      </button>
      <div className="relative flex items-center gap-4 pr-10">
        {icon && (
          <span className="w-14 h-14 flex-shrink-0 rounded-2xl bg-white text-primary flex items-center justify-center ring-4 ring-white/15">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-extrabold text-white leading-tight m-0 truncate">{titulo}</h2>
          {subtitulo && <p className="text-sm text-blue-100 m-0 mt-0.5 truncate">{subtitulo}</p>}
          {chips && <div className="flex flex-wrap items-center gap-2 mt-2">{chips}</div>}
        </div>
      </div>
      {datos.length > 0 && (
        <div className="relative mt-5 flex items-end justify-between gap-4 pt-4 border-t border-white/10">
          {datos.map((d, i) => (
            <div key={d.label} className={i === datos.length - 1 && datos.length > 1 ? 'text-right' : ''}>
              <p className="text-[11px] font-bold uppercase tracking-widest text-secondary m-0">{d.label}</p>
              <p className="text-2xl font-extrabold m-0 tabular-nums">{d.value}</p>
              {d.sub && <p className="text-sm text-blue-100 m-0">{d.sub}</p>}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}

/** Chip sobre la cabecera oscura. */
export const ChipCabecera = ({ activo = true, children }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${activo ? 'bg-white/15 text-white' : 'bg-white/10 text-blue-200'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${activo ? 'bg-secondary' : 'bg-blue-300'}`} /> {children}
  </span>
);

/** Indicador en tarjeta blanca, como los de la ficha de usuario. */
export const Kpi = ({ icon, label, value, ok = true, alerta = false }) => (
  <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-gray-100 min-w-0">
    <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
      ${alerta ? 'bg-secondary/40 text-[#5c4300]' : ok ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-400'}`}>{icon}</span>
    <div className="min-w-0">
      <p className="text-xl font-extrabold text-gray-900 leading-none m-0 tabular-nums truncate">{value}</p>
      <p className="text-[11px] font-semibold text-gray-500 m-0 mt-1 leading-tight">{label}</p>
    </div>
  </div>
);

/** Etiqueta de pestaña con contador, igual a la de la ficha de usuario. */
export const Etiqueta = ({ icon, texto, n }) => (
  <span className="inline-flex items-center gap-2">
    {icon} {texto}
    {n > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold inline-flex items-center justify-center">{n}</span>}
  </span>
);

/**
 * Lista de los talleres a los que se aplica una acción masiva. `detalle(t)` es el
 * dato chico de la derecha (ciclo, horarios…).
 */
export const ListaTalleres = ({ talleres = [], detalle }) => (
  <div className="rounded-2xl border border-gray-100 bg-gray-50 max-h-52 overflow-y-auto divide-y divide-gray-100">
    {talleres.map(t => (
      <div key={t._id} className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 m-0 truncate">{t.nombre}</p>
          <p className="text-xs text-gray-500 m-0 truncate">{t.recinto?.nombre || 'Sin recinto'}</p>
        </div>
        {detalle && <span className="flex-shrink-0 text-xs font-semibold text-gray-500">{detalle(t)}</span>}
      </div>
    ))}
  </div>
);

/** Rótulo de un paso numerado dentro de un modal de varios pasos. */
export const Paso = ({ n, titulo, ayuda }) => (
  <div className="mb-2">
    <p className="flex items-center gap-2 m-0 text-[13px] font-bold text-gray-900">
      <span className="w-5 h-5 flex-shrink-0 rounded-full bg-primary !text-white text-[11px] font-extrabold inline-flex items-center justify-center">{n}</span>
      {titulo}
    </p>
    {ayuda && <p className="text-xs text-gray-500 m-0 mt-0.5 ml-7">{ayuda}</p>}
  </div>
);

export const Vacio =({ titulo, texto, children }) => (
  <div className="py-10 text-center">
    <p className="font-bold text-gray-900 m-0">{titulo}</p>
    {texto && <p className="text-sm text-gray-500 m-0 mt-1">{texto}</p>}
    {children && <div className="mt-4">{children}</div>}
  </div>
);

/** Ocupación de un horario: texto + barra. Rojo en sobrecupo, amarillo desde 80 %. */
export function BarraCupos({ horario, ancho = 'w-40' }) {
  const ocp = horario.cuposOcupados || 0;
  const res = horario.cuposReservados || 0;
  if (horario.cuposIlimitados) {
    return (
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <Chip tono="azul">∞ Ilimitado</Chip>
        <span className="text-[11px] text-gray-500">{ocp} inscrito{ocp !== 1 ? 's' : ''}</span>
      </div>
    );
  }
  const total = horario.cupos || 0;
  const disp = Math.max(0, total - ocp - res);
  const sob = ocp > total;
  const pct = total > 0 ? Math.min(Math.round(((ocp + res) / total) * 100), 100) : 0;
  const color = sob ? 'bg-red-500' : pct >= 80 ? 'bg-secondary' : 'bg-primary';
  return (
    <div className={`flex flex-col items-start sm:items-end gap-1.5 flex-shrink-0 ${ancho}`}>
      {sob ? (
        <Chip tono="rojo">Sobrecupo {ocp}/{total}</Chip>
      ) : (
        <p className="m-0 text-sm text-gray-500">
          <span className="text-lg font-extrabold text-gray-900 tabular-nums">{disp}</span> / {total} disponibles
        </p>
      )}
      <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="m-0 text-[11px] text-gray-500">
        {ocp} inscrito{ocp !== 1 ? 's' : ''}
        {res > 0 && <span className="text-[#5c4300] font-semibold"> · {res} reserva{res !== 1 ? 's' : ''}</span>}
      </p>
    </div>
  );
}

// Nombres que entiende la administración para los campos del taller.
export const CAMPOS = {
  nombre: 'Nombre',
  descripcion: 'Descripción',
  tipoActividad: 'Tipo de actividad',
  recinto: 'Recinto',
  edadMin: 'Edad mínima',
  edadMax: 'Edad máxima',
  nivelIntensidad: 'Intensidad',
  inscripcionInicio: 'Apertura de inscripciones',
  inscripcionFin: 'Cierre de inscripciones',
  actividadesInicio: 'Inicio de actividades',
  actividadesFin: 'Fin de actividades',
  liberarCuposPorBaja: 'Liberar el cupo cuando alguien se da de baja',
  soloTarjetaVecino: 'Solo con Tarjeta Vecino',
  requiereMatricula: 'Requiere matrícula',
  tieneIVA: 'Tiene IVA',
  tipoTaller: 'Tipo de taller',
  tipoDuracionSuscripcion: 'Duración de la suscripción',
  horarios: 'Horarios',
  modalidadPago: 'Precios',
  imagen: 'Imagen',
  usuariosAutorizados: 'Personas autorizadas',
  estado: 'Estado',
  inscripcionesEstado: 'Inscripciones',
};
export const etiquetaCampo = (campo) => CAMPOS[campo]
  || String(campo).replace(/([A-Z])/g, ' $1').replace(/^./, (m) => m.toUpperCase());
export const textoValor = (v) => (v === true ? 'Sí' : v === false ? 'No' : (v == null || v === '') ? 'Sin valor' : String(v));

// Un campo que cambia: lo que hay hoy y lo que se propone, lado a lado.
export const Diff = ({ titulo, antes, propuesto, etiquetas = ['Hoy', 'Propuesto'] }) => (
  <div className="rounded-2xl bg-white border border-gray-100 p-4">
    <p className={claseTituloSeccion}>{titulo}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
      <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 m-0 mb-1.5">{etiquetas[0]}</p>
        <div className="text-sm text-gray-600 break-words">{antes}</div>
      </div>
      <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-primary m-0 mb-1.5">{etiquetas[1]}</p>
        <div className="text-sm font-semibold text-gray-900 break-words">{propuesto}</div>
      </div>
    </div>
  </div>
);
